import {
  useDeleteMutation,
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import {
  BILLING_CALCULATIONS,
  CREDIT_NOTE,
  DEBIT_NOTE,
  OFFLINE_PAYMENT,
  UTILITY_CONSUMER,
} from "tagTypes";

const useUtilityConsumerQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/utility-consumer",
    query,
    tags: [UTILITY_CONSUMER],
  });

export const useLazyGetUtilityConsumerQuery = () => {
  const [getConsumerNumbers, data] = useLazyGetQuery();
  const consumerNumbers = async (query = {}) => {
    return await getConsumerNumbers(
      {
        endpoint: "/utility-consumer",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  };
  return [consumerNumbers, data];
};

const useUtilityConsumerContactInfoQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/utility-consumer/contact-information",
    query,
    tags: [UTILITY_CONSUMER],
  });

export const useGetUtilityConsumerApplicationsQuery = (utilityConsumerId) =>
  useGetQuery({
    endpoint: `/utility-consumer/utility-consumer-application/${utilityConsumerId}`,
    tags: [UTILITY_CONSUMER],
  });

export const useGetUtilityConsumerDetailByConsumerIdQuery = (
  consumerId,
  query = {},
  isOtherUtility = false
) =>
  useGetQuery({
    endpoint: isOtherUtility
      ? `other-utility-consumer/${consumerId}`
      : `/power/utility-consumer/${consumerId}`,
    query,
    tags: [UTILITY_CONSUMER],
  });

export const useLazyGetBillDetailsWithConsumerNumber = () => {
  const [getBillDetails, data] = useLazyGetQuery();
  const getConsumerBillDetails = async (subscriptionType, query = {}) =>
    await getBillDetails({
      endpoint: `/subscription-type/${subscriptionType}/unpaid-bills`,
      query,
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [getConsumerBillDetails, data];
};

export const useLazyGetBillDetailsWithConsumerNumberAndUtilityId = () => {
  const [getBillDetails, data] = useLazyGetQuery();
  const getConsumerBillDetails = async (utilityId, query = {}) =>
    await getBillDetails({
      endpoint: `/utility/${utilityId}/unpaid-bills`,
      query,
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [getConsumerBillDetails, data];
};

export const useLazyGetApplicationNumbers = () => {
  const [getBillDetails, data] = useLazyGetQuery();
  const getConsumerBillDetails = async (subscriptionType, query = {}) =>
    await getBillDetails({
      endpoint: `/subscription-type/${subscriptionType}/unpaid-demand-estimate-bills`,
      query,
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [getConsumerBillDetails, data];
};

export const useLazyGetConsumerNumberByGroupId = () => {
  const [getConsumerNumber, data] = useLazyGetQuery();
  const getCOnsumerNumberById = async (query = {}) =>
    await getConsumerNumber({
      endpoint: "/consumer-number",
      query,
    }).unwrap();
  return [getCOnsumerNumberById, data];
};

const useLazyUtilityConsumerQuery = () => {
  const [getUtilityConsumer, data] = useLazyGetQuery();
  const utilityConsumer = async (consumerId, query = {}) =>
    await getUtilityConsumer(
      {
        endpoint: `/power/utility-consumer/${consumerId}`,
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [utilityConsumer, data];
};

const useGetMeterAssignByUtilityConsumerId = (utilityConsumerId) =>
  useGetQuery({
    endpoint: `/utility-consumer/${utilityConsumerId}/meter-assign-tracking`,
    tags: [UTILITY_CONSUMER],
  });

export const useGetUtilityConsumerLoadTrackingDetails = (utilityConsumerId) =>
  useGetQuery({
    endpoint: `/utility-consumer/${utilityConsumerId}/consumer-details-tracking`,
    tags: [UTILITY_CONSUMER],
  });

const useGetConsumerCategoryWiseQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/consumer-category-wise",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazyConsumerCategoryWiseQuery = () => {
  const [getConsumerCategory, data] = useLazyGetQuery();
  const consumerCategory = async (query = {}) =>
    await getConsumerCategory(
      {
        endpoint: "/report/consumer-category-wise",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [consumerCategory, data];
};

const useGetConsumerTariffWiseQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/consumer-tariff-wise",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazyConsumerTariffWiseWiseQuery = () => {
  const [getConsumerTariff, data] = useLazyGetQuery();
  const ConsumerTariff = async (query = {}) =>
    await getConsumerTariff(
      {
        endpoint: "/report/consumer-tariff-wise",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [ConsumerTariff, data];
};

const useGetConsumerActivatedDisconnectedCategoryWiseQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/consumer-activated-disconnected-category-wise",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazyConsumerActivatedDisconnectedCategoryWiseQuery = () => {
  const [getConsumerActivatedDisconnectedCategoryWise, data] =
    useLazyGetQuery();
  const consumerActivatedDisconnectedCategoryWise = async (query = {}) =>
    await getConsumerActivatedDisconnectedCategoryWise(
      {
        endpoint: "/report/consumer-activated-disconnected-category-wise",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [consumerActivatedDisconnectedCategoryWise, data];
};

const useGetConsumerActivatedDisconnectedTariffWiseQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/consumer-activated-disconnected-Tariff-wise",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useGetLazyConsumerActivatedDisconnectedTariffWiseQuery = () => {
  const [getConsumerActivatedDisconnectedTariffWise, data] = useLazyGetQuery();
  const fetchConsumerActivatedDisconnectedTariffWise = async (query = {}) =>
    await getConsumerActivatedDisconnectedTariffWise(
      {
        endpoint: "/report/consumer-activated-disconnected-Tariff-wise",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [fetchConsumerActivatedDisconnectedTariffWise, data];
};

const useGetSopConsumerCategoryQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/report/sop-gerc",
    query,
    tags: [UTILITY_CONSUMER],
  });

const useLazyGetDebitNotesDetailsByDebitNoteIdQuery = () => {
  const [getDebitNOteDetails, data] = useLazyGetQuery();
  const getDebitNotesData = async (id) =>
    await getDebitNOteDetails(
      {
        endpoint: `/debit-note/${id}`,
        tags: [UTILITY_CONSUMER, DEBIT_NOTE],
      },
      true
    ).unwrap();
  return [getDebitNotesData, data];
};

const useLazyGetCreditNotesDetailsByCreditNoteIdQuery = () => {
  const [getDebitNOteDetails, data] = useLazyGetQuery();
  const getDebitNotesData = async (id) =>
    await getDebitNOteDetails(
      {
        endpoint: `/credit-note/${id}`,
        tags: [UTILITY_CONSUMER, CREDIT_NOTE],
      },
      true
    ).unwrap();
  return [getDebitNotesData, data];
};

const useGetLazySopConsumerCategoryQuery = () => {
  const [getSopConsumerCategory, data] = useLazyGetQuery();
  const fetchSopConsumerCategoryWise = async (query = {}) =>
    await getSopConsumerCategory(
      {
        endpoint: "/report/sop-gerc",
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  return [fetchSopConsumerCategoryWise, data];
};

const usePutUtilityConsumerMutation = () => {
  const [utilityConsumer, data] = usePutMutation();
  const addUtilityConsumer = async (payload, query = {}) =>
    await utilityConsumer({
      endpoint: "/power/utility-consumer",
      query,
      payload,
      toastMessage: "Utility consumer updated successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [addUtilityConsumer, data];
};

export const usePutUpdateOtherUtilityConsumerMutation = () => {
  const [utilityConsumer, data] = usePutMutation();
  const updateUtilityConsumer = async (payload, query = {}) =>
    await utilityConsumer({
      endpoint: "/other-utility-consumer",
      query,
      payload,
      toastMessage: "Utility consumer updated successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [updateUtilityConsumer, data];
};

export const usePutDisconnectUtilityConsumerPowerStatusMutation = () => {
  const [disconnectUtilityConsumerPower, data] = usePutMutation();
  const updatePowerStatus = async (payload, query = {}) =>
    await disconnectUtilityConsumerPower({
      endpoint: "utility-consumer/temporary-disconnect-connection",
      query,
      payload,
      toastMessage: "Status updated successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [updatePowerStatus, data];
};

export const usePutActivateConsumerPowerStatusMutation = () => {
  const [activateUtilityConsumerPower, data] = usePutMutation();
  const updatePowerStatus = async (payload, query = {}) =>
    await activateUtilityConsumerPower({
      endpoint: "utility-consumer/activate-connection",
      query,
      payload,
      toastMessage: "Status updated successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [updatePowerStatus, data];
};

const useLazyUtilityConsumerValidateQuery = () => {
  const [validateConsumer, data] = useLazyGetQuery();

  const consumerStatus = async (customerNumber, customerName, query = {}) =>
    await validateConsumer({
      endpoint: `/${customerNumber}/${customerName}/utility-consumer`,
      query,
    }).unwrap();
  return [consumerStatus, data];
};

export const usePostDisconnectedConsumerPaymentMutation = () => {
  const [dispatchBills, data] = usePostMutation();
  const setBillsPdf = async (payload) =>
    await dispatchBills({
      endpoint:
        "utility-consumer/temporary-disconnected-connection/payment-url",
      payload,
      tags: [BILLING_CALCULATIONS],
    }).unwrap();
  return [setBillsPdf, data];
};

export const usePostOfflinePaymentDetailsMutation = () => {
  const [addPaymentDetails, data] = usePostMutation();
  const postPaymentDetails = async (payload) =>
    await addPaymentDetails({
      endpoint: "/payment-transaction/offline-payment-collection",
      payload,
      toastMessage: "Payment completed successfully",
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [postPaymentDetails, data];
};

export const usePostDemandEstimateOfflinePaymentDetailsMutation = () => {
  const [addPaymentDetails, data] = usePostMutation();
  const postPaymentDetails = async (payload) =>
    await addPaymentDetails({
      endpoint:
        "/payment-transaction/demand-estimate-offline-payment-collection",
      payload,
      toastMessage: "Payment completed successfully",
      tags: [OFFLINE_PAYMENT],
    }).unwrap();
  return [postPaymentDetails, data];
};

export const usePostAddRefundDetailsMutation = () => {
  const [addRefundDetails, data] = usePostMutation();
  const postRefundDetails = async (utilityConsumerId, payload) =>
    await addRefundDetails({
      endpoint: `/utility-consumer/${utilityConsumerId}/consumer-refund-completion`,
      payload,
      toastMessage: "Consumer disconnection process completed successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [postRefundDetails, data];
};

export const usePostUtilityConsumerDisconnectionPaymentDetailsMutation = () => {
  const [addPaymentDetails, data] = usePostMutation();
  const postPaymentDetails = async (payload) =>
    await addPaymentDetails({
      endpoint:
        "/payment-transaction/demand-estimate-offline-payment-collection",
      payload,
      toastMessage: "Payment completed successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [postPaymentDetails, data];
};

const usePostSendMailMutation = () => {
  const [sendMail, data] = usePostMutation();
  const setSendMail = async (payload) =>
    await sendMail({
      endpoint: "/mail/send-mail",
      payload: payload || {},
      toastMessage: "Email Sent successfully",
      tags: [UTILITY_CONSUMER],
    }).unwrap();
  return [setSendMail, data];
};

const usePostDebitNoteFileMutation = () => {
  const [uploadDebitNote, data] = usePostMutation();
  const addDebitNote = async (payload) =>
    await uploadDebitNote({
      endpoint: "/debit-note/upload-excel",
      payload: payload || {},
      toastMessage: "Debit note file uploaded successfully",
      tags: [UTILITY_CONSUMER, DEBIT_NOTE],
    }).unwrap();
  return [addDebitNote, data];
};

const usePostSendDebitNoticeMutation = () => {
  const [sendNotice, data] = usePostMutation();
  const sendDebitNotice = async (id) =>
    await sendNotice({
      endpoint: `/debit-note/${id}/send-notice-mail`,
      toastMessage: "Debit note notice sent successfully",
      tags: [UTILITY_CONSUMER, DEBIT_NOTE],
    }).unwrap();
  return [sendDebitNotice, data];
};

const usePostDebitNoteDetailsMutation = () => {
  const [addDebitData, data] = usePostMutation();
  const addDebitNoteDetails = async (payload) =>
    await addDebitData({
      endpoint: "/debit-note",
      payload: payload || {},
      toastMessage: "Debit note added successfully",
      tags: [DEBIT_NOTE],
    }).unwrap();
  return [addDebitNoteDetails, data];
};


const usePostCreditNoteDetailsMutation = () => {
  const [addDebitData, data] = usePostMutation();
  const addDebitNoteDetails = async (payload) =>
    await addDebitData({
      endpoint: "/credit-note",
      payload: payload || {},
      toastMessage: !payload?.IsPreview
        ? payload?.id
          ? "Credit note updated successfully"
          : "Credit note added successfully"
        : "",
      tags: [CREDIT_NOTE],
    }).unwrap();
  return [addDebitNoteDetails, data];
};

export const useDeleteDebitNoteMutation = () => {
  const [debitNote, data] = useDeleteMutation();
  const deleteDebitNote = async (id, query = {}) =>
    await debitNote({
      endpoint: `/debit-note/${id}`,
      query,
      toastMessage: "Debit note deleted successfully",
      tags: [DEBIT_NOTE],
    }).unwrap();
  return [deleteDebitNote, data];
};

export const useDeleteCreditNoteMutation = () => {
  const [debitNote, data] = useDeleteMutation();
  const deleteDebitNote = async (id, query = {}) =>
    await debitNote({
      endpoint: `/credit-note/${id}`,
      query,
      toastMessage: "Credit note deleted successfully",
      tags: [CREDIT_NOTE],
    }).unwrap();
  return [deleteDebitNote, data];
};
export const useLazyGetLatestAuthorizedUser = () => {
  const [getLatestAuthorizedUser, data] = useLazyGetQuery();
  const authorizedUserDetails = async (utilityConsumerId, query = {}) => {
    return await getLatestAuthorizedUser(
      {
        endpoint: `/utility-consumer/${utilityConsumerId}/user`,
        query,
        tags: [UTILITY_CONSUMER],
      },
      true
    ).unwrap();
  };
  return [authorizedUserDetails, data];
};

export const useLazyGetOtherUtilityConsumerNumberOptionsByUtilityId = () => {
  const [getConsumerNumber, data] = useLazyGetQuery();
  const getConsumerNumberOptions = async (utilityId, query = {}) =>
    await getConsumerNumber({
      endpoint: `/utility-wise-other-consumer/${utilityId}`,
      query,
      tags: [],
    }).unwrap();
  return [getConsumerNumberOptions, data];
};

const usePutCreditNoteDetailsMutation = () => {
  const [creditNote, data] = usePutMutation();
  const approvedCreditNote = async (creditNoteId, query = {}) =>
    await creditNote({
      endpoint: `/credit-note/approve/${creditNoteId}`,
      query,
      toastMessage: "Credit note approved successfully",
      tags: [CREDIT_NOTE],
    }).unwrap();
  return [approvedCreditNote, data];
};

export {
  useUtilityConsumerQuery,
  useGetConsumerCategoryWiseQuery,
  useGetConsumerTariffWiseQuery,
  useGetConsumerActivatedDisconnectedCategoryWiseQuery,
  useGetConsumerActivatedDisconnectedTariffWiseQuery,
  useGetSopConsumerCategoryQuery,
  usePostDebitNoteDetailsMutation,
  useLazyUtilityConsumerQuery,
  usePutUtilityConsumerMutation,
  useLazyUtilityConsumerValidateQuery,
  usePostSendDebitNoticeMutation,
  useGetLazyConsumerCategoryWiseQuery,
  useGetLazyConsumerTariffWiseWiseQuery,
  useGetLazyConsumerActivatedDisconnectedCategoryWiseQuery,
  useGetLazySopConsumerCategoryQuery,
  useGetLazyConsumerActivatedDisconnectedTariffWiseQuery,
  useLazyGetDebitNotesDetailsByDebitNoteIdQuery,
  useLazyGetCreditNotesDetailsByCreditNoteIdQuery,
  usePostCreditNoteDetailsMutation,
  useGetMeterAssignByUtilityConsumerId,
  useUtilityConsumerContactInfoQuery,
  usePostSendMailMutation,
  usePostDebitNoteFileMutation,
  usePutCreditNoteDetailsMutation,
};
